import cn from "@/v2-ui/utils/utils.cn"
import StatusSummaryRowSkeleton from "@/v2-status-client/status/skeleton/StatusSummaryRowSkeleton"

export type StatusPlatformGroupSkeletonProps = {
  // Number of category rows rendered under the placeholder
  // header. Defaults to 3 because that's the median across our
  // four real platforms; callers stagger this so the page
  // doesn't end up with four identical-height cards.
  rowCount?: number
  // Width hint for the platform header title placeholder. The
  // top-level page composer overrides this per card so the
  // skeleton column reads as a list of varied platform names
  // instead of a column of clones.
  headerTitleWidth?: string
}

// Stand-in for `StatusPlatformGroup` -- a card with a header row
// followed by N category placeholder rows. Same outer chrome
// (rounded border, divider, inner padding) as the real card so
// when the data lands the layout snaps cleanly into place.
function StatusPlatformGroupSkeleton(
  props: Readonly<StatusPlatformGroupSkeletonProps>,
) {
  const { rowCount = 3, headerTitleWidth = "w-32" } = props
  return (
    <article
      aria-hidden
      className={cn(
        "rounded-lg border border-border/60 bg-background/30",
        "overflow-hidden",
      )}
    >
      <StatusSummaryRowSkeleton
        asHeader
        titleWidth={headerTitleWidth}
        showUptime
      />
      <div className="flex flex-col px-4 sm:px-5">
        {Array.from({ length: rowCount }).map((_, i) => (
          <StatusSummaryRowSkeleton
            key={i}
            // Stagger widths so the rows don't look stamped from
            // a single template. The exact widths don't matter --
            // they just need to vary.
            titleWidth={ROW_TITLE_WIDTHS[i % ROW_TITLE_WIDTHS.length]}
            showUptime
          />
        ))}
      </div>
    </article>
  )
}

const ROW_TITLE_WIDTHS = ["w-32", "w-40", "w-28", "w-44", "w-36"] as const

export default StatusPlatformGroupSkeleton
